import { memo } from "react";
import { Amount } from "@design-system/Amount/Amount";
import { formatBusinessDate, formatDateOnly } from "@helpers/date";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { useEntitiesById } from "@hooks/useEntities";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import classNames from "classnames";
import { TransactionDirectionType } from "@common/categories";
import { useCategoriesById } from "@hooks/useCategoriesById";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { CategoryIcon } from "../CategoryIcon";
import styles from "./TransactionPreview.module.scss";

export interface TransactionPreviewData {
    id: number;
    date: string | Date;
    dateOnly?: string | null;
    bankDescription: string;
    displayDescription: string | null;
    categoryId?: string;
    entityId?: number;
    financialAccountId?: number;
    amount: number;
}

interface PairedTransferLike {
    id: number;
    date: string | Date;
    dateOnly?: string | null;
    bankDescription: string;
    displayDescription: string | null;
    categoryId?: string;
    entityId?: number;
    financialAccountId?: number;
    amount: number;
}

/**
 * Maps a `pairedTransfer` (or any transaction shape with the same fields) to
 * `TransactionPreviewData`. Shared by every cell that shows a match preview
 * (transfer match cell, category cell, GL ledger cell) so the mapping stays
 * in one place.
 */
export function toTransactionPreviewData(
    pairedTransfer: PairedTransferLike | null | undefined,
): TransactionPreviewData | null {
    if (!pairedTransfer) {
        return null;
    }
    return {
        id: pairedTransfer.id,
        date: pairedTransfer.date,
        dateOnly: pairedTransfer.dateOnly,
        bankDescription: pairedTransfer.bankDescription,
        displayDescription: pairedTransfer.displayDescription,
        categoryId: pairedTransfer.categoryId,
        entityId: pairedTransfer.entityId,
        financialAccountId: pairedTransfer.financialAccountId,
        amount: pairedTransfer.amount,
    };
}

interface TransactionPreviewProps {
    transaction: TransactionPreviewData;
    className?: string;
}

export const TransactionPreview = memo(
    ({ transaction, className }: TransactionPreviewProps) => {
        const categoriesById = useCategoriesById();
        const financialAccounts = useFinancialAccounts();
        const entities = useEntitiesById();
        const { enabled: isGlFirstEnabled } = useIsGlFirst();

        const category = transaction.categoryId
            ? categoriesById[transaction.categoryId]
            : undefined;

        const entity =
            transaction.entityId !== undefined
                ? entities.get(transaction.entityId)
                : undefined;

        const account = financialAccounts.find(
            ({ id }) => id === transaction.financialAccountId,
        );

        const transactionDirection =
            transaction.amount > 0
                ? TransactionDirectionType.incoming
                : TransactionDirectionType.outgoing;

        return (
            <div className={classNames(styles.container, className)}>
                <span className={styles.date}>
                    {transaction.dateOnly
                        ? formatDateOnly(transaction.dateOnly)
                        : formatBusinessDate(new Date(transaction.date))}
                </span>
                <span className={styles.description}>
                    <Tooltip content={transaction.bankDescription}>
                        <span>
                            <BreakableText
                                text={transaction.displayDescription ?? ""}
                            />
                        </span>
                    </Tooltip>
                </span>
                {!isGlFirstEnabled && transaction.categoryId && (
                    <span className={styles.category}>
                        {category ? (
                            <Tooltip content={category.label}>
                                <span className="icon-color-revert">
                                    <CategoryIcon
                                        categoryId={transaction.categoryId}
                                        transactionDirection={
                                            transactionDirection
                                        }
                                    />
                                </span>
                            </Tooltip>
                        ) : (
                            <CategoryIcon
                                categoryId={transaction.categoryId}
                                transactionDirection={transactionDirection}
                            />
                        )}
                    </span>
                )}
                {account && (
                    <AccountCard
                        account={account}
                        onlyNumber
                        iconSize="xs"
                        displayNameAsTooltip
                        className={styles.account}
                        maxNameLength={6}
                    />
                )}
                <div
                    className={classNames(
                        "transaction-amount",
                        "transaction-amount--color",
                        {
                            "transaction-amount--credit":
                                transaction.amount > 0,
                            "transaction-amount--debit": transaction.amount < 0,
                        },
                        styles.amount,
                    )}
                >
                    <Amount
                        amount={transaction.amount}
                        showNegative
                        className={styles.amount}
                    />
                </div>
                {entity && (
                    <EntityAvatar
                        entity={entity}
                        className={styles.entity}
                        size="xs"
                    />
                )}
            </div>
        );
    },
);
